import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { useServicesList } from '@/hooks/useServices';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard, SaveBar } from '@/pages/settings/ClinicGeneral';
import type { Json } from '@/types/database.generated';

// ─── Types ────────────────────────────────────────────────────────────────────

const WEEKDAYS = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lordag', 'sondag'] as const;
type Weekday = (typeof WEEKDAYS)[number];

interface DayHours {
  enabled: boolean;
  start: string;
  end: string;
}

type BookingHoursShape = Record<Weekday, DayHours>;

const DEFAULT_BOOKING_HOURS: BookingHoursShape = {
  mandag: { enabled: true, start: '08:00', end: '16:00' },
  tirsdag: { enabled: true, start: '08:00', end: '16:00' },
  onsdag: { enabled: true, start: '08:00', end: '16:00' },
  torsdag: { enabled: true, start: '08:00', end: '16:00' },
  fredag: { enabled: true, start: '08:00', end: '16:00' },
  lordag: { enabled: false, start: '09:00', end: '14:00' },
  sondag: { enabled: false, start: '09:00', end: '14:00' },
};

function readBookingHours(value: Json): BookingHoursShape {
  const out = { ...DEFAULT_BOOKING_HOURS };
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const day of WEEKDAYS) {
      const v = (value as Record<string, Json>)[day];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const obj = v as Record<string, Json>;
        out[day] = {
          enabled: Boolean(obj.enabled),
          start: typeof obj.start === 'string' ? obj.start : DEFAULT_BOOKING_HOURS[day].start,
          end: typeof obj.end === 'string' ? obj.end : DEFAULT_BOOKING_HOURS[day].end,
        };
      }
    }
  }
  return out;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ClinicOnlineBooking() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading: clinicLoading, error: clinicError } = useClinic(activeClinicId);
  const { data: services = [] } = useServicesList(activeClinicId, { includeInactive: false });
  const { data: members = [] } = useClinicMembers(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  // ─── Local state ────────────────────────────────────────────────────────────

  const [enabled, setEnabled] = useState(false);
  const [advanceDays, setAdvanceDays] = useState(60);
  const [minHoursBefore, setMinHoursBefore] = useState(2);
  const [hours, setHours] = useState<BookingHoursShape>(DEFAULT_BOOKING_HOURS);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedClinicianIds, setSelectedClinicianIds] = useState<string[]>([]);

  // ─── Seed from clinic data ───────────────────────────────────────────────────
  // Only seed the form when the component first mounts or clinic ID changes,
  // NOT every time clinic data updates (which happens after save)
  useEffect(() => {
    if (!clinic) return;
    const c = clinic as typeof clinic & {
      online_booking_enabled?: boolean;
      online_booking_advance_days?: number;
      online_booking_min_hours_before?: number;
      online_booking_hours?: Json;
      online_booking_services?: string[];
      online_booking_clinicians?: string[];
    };
    setEnabled(c.online_booking_enabled ?? false);
    setAdvanceDays(c.online_booking_advance_days ?? 60);
    setMinHoursBefore(c.online_booking_min_hours_before ?? 2);
    setHours(readBookingHours(c.online_booking_hours ?? {}));
    setSelectedServiceIds(c.online_booking_services ?? []);
    setSelectedClinicianIds(c.online_booking_clinicians ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]); // Only re-run when clinic ID changes, not when clinic data changes

  if (clinicLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (clinicError) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        online_booking_enabled: enabled,
        online_booking_advance_days: advanceDays,
        online_booking_min_hours_before: minHoursBefore,
        online_booking_hours: hours as unknown as Json,
        online_booking_services: selectedServiceIds as unknown as Json,
        online_booking_clinicians: selectedClinicianIds as unknown as Json,
      };
      await update.mutateAsync(payload);
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update clinic online booking settings failed', err);
      const errorMessage =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : '';
      if (errorMessage.includes('forbidden') || errorMessage.includes('admin required')) {
        toast.error(t('ob_settings.admin_required_error'));
      } else {
        toast.error(t('settings.save_error_toast'));
      }
    }
  };

  const toggleServiceId = (id: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleClinicianId = (id: string) => {
    setSelectedClinicianIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // ─── Public booking URL ─────────────────────────────────────────────────────
  const bookingUrl = `${window.location.origin}/book/${(clinic as { slug?: string }).slug ?? ''}`;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Enable / disable */}
      <SectionCard
        title={t('ob_settings.enable_title')}
        subtitle={t('ob_settings.enable_subtitle')}
        readOnly={!canEdit}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t('ob_settings.enable_label')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('ob_settings.enable_desc')}
            </p>
          </div>
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => setEnabled((v) => !v)}
            className={[
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent',
              'transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-60',
              enabled ? 'bg-indigo-600' : 'bg-gray-200',
            ].join(' ')}
          >
            <span
              className={[
                'inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                enabled ? 'translate-x-5' : 'translate-x-0',
              ].join(' ')}
            />
          </button>
        </div>

        {enabled && (
          <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50 p-3 dark:border-indigo-800 dark:bg-indigo-900/30">
            <p className="text-xs text-indigo-700 dark:text-indigo-300">
              {t('ob_settings.booking_url_label')}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <Globe className="h-4 w-4 flex-shrink-0 text-indigo-500" />
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate font-mono text-sm text-indigo-600 hover:underline dark:text-indigo-400"
              >
                {bookingUrl}
              </a>
            </div>
          </div>
        )}
      </SectionCard>

      {/* Advance days & min hours before */}
      <SectionCard
        title={t('ob_settings.limits_title')}
        subtitle={t('ob_settings.limits_subtitle')}
        readOnly={!canEdit}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('ob_settings.advance_days_label')}
            </label>
            <input
              type="number"
              min={1}
              max={365}
              value={advanceDays}
              disabled={!canEdit}
              onChange={(e) => setAdvanceDays(Number(e.target.value))}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-400">{t('ob_settings.advance_days_hint')}</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('ob_settings.min_hours_label')}
            </label>
            <input
              type="number"
              min={0}
              max={72}
              value={minHoursBefore}
              disabled={!canEdit}
              onChange={(e) => setMinHoursBefore(Number(e.target.value))}
              className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-400">{t('ob_settings.min_hours_hint')}</p>
          </div>
        </div>
      </SectionCard>

      {/* Per-weekday hours */}
      <SectionCard
        title={t('ob_settings.hours_title')}
        subtitle={t('ob_settings.hours_subtitle')}
        readOnly={!canEdit}
      >
        <div className="space-y-4">
          {WEEKDAYS.map((day) => {
            const dh = hours[day];
            return (
              <div
                key={day}
                className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="w-32">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={dh.enabled}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], enabled: e.target.checked },
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                    />
                    <span className="text-sm font-medium capitalize text-gray-900 dark:text-white">
                      {t(`settings.hours.weekdays.${day}`)}
                    </span>
                  </label>
                </div>

                {dh.enabled ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="time"
                      value={dh.start}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], start: e.target.value },
                        }))
                      }
                      className="h-11 w-32 rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t('settings.hours.to')}
                    </span>
                    <input
                      type="time"
                      value={dh.end}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setHours((h) => ({
                          ...h,
                          [day]: { ...h[day], end: e.target.value },
                        }))
                      }
                      className="h-11 w-32 rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                ) : (
                  <span className="text-sm italic text-gray-400">{t('settings.hours.closed')}</span>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Bookable services */}
      <SectionCard
        title={t('ob_settings.services_title')}
        subtitle={t('ob_settings.services_subtitle')}
        readOnly={!canEdit}
      >
        {services.length === 0 ? (
          <p className="text-sm text-gray-400">{t('ob_settings.services_empty')}</p>
        ) : (
          <div className="space-y-2">
            {services.map((svc) => (
              <label
                key={svc.id}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 p-3 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
              >
                <input
                  type="checkbox"
                  checked={selectedServiceIds.includes(svc.id)}
                  disabled={!canEdit}
                  onChange={() => toggleServiceId(svc.id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div
                  className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: svc.color }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{svc.name}</p>
                  <p className="text-xs text-gray-500">{svc.duration_minutes} min</p>
                </div>
              </label>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Visible clinicians */}
      <SectionCard
        title={t('ob_settings.clinicians_title')}
        subtitle={t('ob_settings.clinicians_subtitle')}
        readOnly={!canEdit}
      >
        {members.length === 0 ? (
          <p className="text-sm text-gray-400">{t('ob_settings.clinicians_empty')}</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <label
                key={m.user_id}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 p-3 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
              >
                <input
                  type="checkbox"
                  checked={selectedClinicianIds.includes(m.user_id)}
                  disabled={!canEdit}
                  onChange={() => toggleClinicianId(m.user_id)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</p>
                  <p className="text-xs text-gray-500">{m.email}</p>
                </div>
              </label>
            ))}
          </div>
        )}
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending} />
    </form>
  );
}
